import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { $convertFromMarkdownString } from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import {
	setScrollableTablesActive,
	TableCellNode,
	TableNode,
	TableRowNode,
} from "@lexical/table";
import type { EditorThemeClasses, LexicalEditor } from "lexical";
import { $nodesOfType, TextNode } from "lexical";
import { useEffect } from "react";
import { cn } from "#/lib/utils";
import { splitEmojiSegments } from "./markdown-emoji";
import { MARKDOWN_TRANSFORMERS } from "./markdown-transformers";

/* 읽기 전용 Lexical 마크다운 렌더러 — react-markdown을 대체한다.
 * CMS(어드민 에디터)와 동일한 노드/변환기 세트로 파싱해 저장 방언과 1:1.
 * markdown.tsx의 lazy() 셸을 통해서만 로드된다 (Lexical을 엔트리 청크에서 격리). */

const MARKDOWN_NODES = [
	HeadingNode,
	QuoteNode,
	ListNode,
	ListItemNode,
	LinkNode,
	AutoLinkNode,
	CodeNode,
	CodeHighlightNode,
	TableNode,
	TableRowNode,
	TableCellNode,
	HorizontalRuleNode,
];

// 구 markdown.tsx 컴포넌트 맵의 클래스 문자열을 그대로 복제 — 시각적 파리티가 목표.
// 테이블 셀 색은 tableCell에 두면 th의 text-foreground와 동률 충돌하므로 tr에서 [&>td]로 적용.
const theme: EditorThemeClasses = {
	paragraph: "mt-3 text-base leading-relaxed text-sea-ink-soft",
	heading: {
		h1: "mt-10 mb-3 text-xl font-semibold text-foreground", // 전처리로 h2가 되지만 벨트로 유지
		h2: "mt-10 mb-3 text-xl font-semibold text-foreground",
		h3: "mt-6 mb-2 text-lg font-semibold text-foreground",
		h4: "mt-6 mb-2 text-lg font-semibold text-foreground",
		h5: "mt-6 mb-2 text-lg font-semibold text-foreground",
		h6: "mt-6 mb-2 text-lg font-semibold text-foreground",
	},
	quote: "my-4 border-l-2 border-border pl-4 italic text-sea-ink-soft",
	list: {
		ul: "mt-3 ml-4 list-disc text-base leading-relaxed text-sea-ink-soft",
		ol: "mt-3 ml-4 list-decimal text-base leading-relaxed text-sea-ink-soft",
		listitem: "my-1",
		nested: { listitem: "list-none" },
	},
	link: "text-foreground underline hover:text-primary",
	text: {
		bold: "font-semibold text-foreground",
		italic: "italic",
		strikethrough: "line-through",
		code: "rounded bg-muted px-1 py-0.5 font-mono text-xs",
	},
	hr: "my-8 border-border",
	table: "w-full border-collapse text-sm text-foreground",
	tableScrollableWrapper:
		"my-5 overflow-x-auto rounded-md border border-border",
	tableRow:
		"border-b border-border last:border-b-0 [&>td]:text-sea-ink-soft [&>td:first-child]:font-medium [&>td:first-child]:text-foreground",
	tableCell: "px-3 py-2 align-top",
	tableCellHeader:
		"bg-foreground/[0.06] text-left text-xs font-semibold uppercase tracking-wide text-foreground",
};

// 페이지가 실제 h1을 소유 — 본문 # 은 ## 로 다운시프트 (구 렌더러의 h1→h2와 동일 정책)
export function preprocessMarkdown(markdown: string): string {
	return markdown.replace(/^# /gm, "## ");
}

/* 컬러 이모지는 color의 알파를 곱해 렌더링됨 — text-sea-ink-soft(알파 .88) 안에서
 * 흐려지지 않게 이모지 조각만 분리해 알파를 1로 복원 (구 rehypeEmojiOpaque와 동일 정책). */
function $markEmojiOpaque() {
	for (const node of $nodesOfType(TextNode)) {
		const segments = splitEmojiSegments(node.getTextContent());
		if (!segments) {
			continue;
		}
		const boundaries: number[] = [];
		let offset = 0;
		for (const segment of segments.slice(0, -1)) {
			offset += segment.text.length;
			boundaries.push(offset);
		}
		const pieces = boundaries.length ? node.splitText(...boundaries) : [node];
		pieces.forEach((piece, index) => {
			if (segments[index]?.emoji) {
				piece.setStyle("color: rgb(from currentColor r g b / 1)");
			}
		});
	}
}

function $parseMarkdown(markdown: string) {
	$convertFromMarkdownString(
		preprocessMarkdown(markdown),
		MARKDOWN_TRANSFORMERS,
	);
	$markEmojiOpaque();
}

// 표시 전용 — md prop 변경(언어 전환 등) 시 루트만 다시 채운다
function SyncMarkdownPlugin({ markdown }: { markdown: string }) {
	const [editor] = useLexicalComposerContext();

	useEffect(() => {
		editor.update(() => {
			$parseMarkdown(markdown);
		});
	}, [editor, markdown]);

	return null;
}

type MarkdownLexicalProps = {
	children: string;
	className?: string;
};

export default function MarkdownLexical({
	children,
	className,
}: MarkdownLexicalProps) {
	return (
		<LexicalComposer
			initialConfig={{
				namespace: "learn-markdown",
				theme,
				nodes: MARKDOWN_NODES,
				editable: false,
				editorState: (editor: LexicalEditor) => {
					// TableNode.createDOM이 최초 DOM 생성 때 스크롤 래퍼 플래그를 읽음 —
					// 플러그인 useEffect는 늦으므로 초기화 콜백에서 켠다
					setScrollableTablesActive(editor, true);
					$parseMarkdown(children);
				},
				onError: (error: Error) => {
					throw error;
				},
			}}
		>
			<div className={cn(className)}>
				<RichTextPlugin
					contentEditable={<ContentEditable className="outline-none" />}
					placeholder={null}
					ErrorBoundary={LexicalErrorBoundary}
				/>
			</div>
			<SyncMarkdownPlugin markdown={children} />
		</LexicalComposer>
	);
}
